import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { events } from 'aws-amplify/data';
import { environment } from '../../../environments/environment';

export interface AppsyncResponse {
  status: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppsyncService {
  private channel: any;
  private subscription: any;
  private isConfigured: boolean = false;

  private async configureAmplify(): Promise<void> {
    if (this.isConfigured) {
      return;
    }

    const { APPSYNC } = environment;
    if (!APPSYNC?.ENDPOINT || !APPSYNC?.REGION || !APPSYNC?.API_KEY) {
      throw new Error('AppSync configuration is missing required environment variables');
    }

    Amplify.configure({
      API: {
        Events: {
          endpoint: APPSYNC.ENDPOINT,
          region: APPSYNC.REGION,
          defaultAuthMode: 'apiKey',
          apiKey: APPSYNC.API_KEY,
        },
      },
    });

    this.isConfigured = true;
  }

  /**
   * Subscribe to a channel and automatically close when status is "published"
   * @param channelNamespace The namespace for the channel
   * @param hashID The hash ID for the channel
   * @param onPublished Optional callback function called when status is "published" before closing
   * @returns Promise that resolves when connection is established
   */
  async subscribeToChannel(
    channelNamespace: string,
    hashID: string,
    onPublished?: (event: AppsyncResponse) => void,
  ): Promise<void> {
    this.cleanupExistingConnection();

    await this.configureAmplify();

    const channelPath = `${channelNamespace}/${hashID}`;
    this.channel = await events.connect(channelPath);
    this.subscription = this.channel.subscribe({
      next: (event: any) => this.handleEvent(event, onPublished),
      error: () => {},
      complete: () => {},
    });
  }

  /**
   * Close the connection and unsubscribe
   * Safe to call multiple times - handles null-safe cleanup
   */
  closeConnection(): void {
    this.unsubscribe();
    this.closeChannel();
  }

  private cleanupExistingConnection(): void {
    if (this.subscription || this.channel) {
      this.closeConnection();
    }
  }

  private handleEvent(event: any, onPublished?: (event: AppsyncResponse) => void): void {
    const eventData = event?.event as AppsyncResponse | undefined;

    if (eventData?.status === 'published') {
      onPublished?.(eventData);
      this.closeConnection();
    }
  }

  private unsubscribe(): void {
    if (!this.subscription) {
      return;
    }

    try {
      this.subscription.unsubscribe();
    } catch (error) {
      // Silently handle unsubscribe errors
    } finally {
      this.subscription = null;
    }
  }

  private closeChannel(): void {
    if (!this.channel) {
      return;
    }

    try {
      this.channel.close();
    } catch (error) {
      // Silently handle close errors
    } finally {
      this.channel = null;
    }
  }
}
